import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCompanies } from "../hooks/useCompanies";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { Badge } from "../../../../ui/components/Badge";
import { TextField } from "../../../../ui/components/TextField";
import {
  FeatherPlus,
  FeatherTrash2,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherSearch,
  FeatherEye,
} from "@subframe/core";

export const CompanyListPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    companies,
    pagination,
    loading,
    error,
    fetchCompanies,
    deleteCompany,
  } = useCompanies();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [deletingCompanyId, setDeletingCompanyId] = useState<number | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when user starts typing (searchTerm changes)
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) {
      setPage(1);
    }
  }, [searchTerm, debouncedSearchTerm]);

  // Fetch companies when page or debounced search term changes
  useEffect(() => {
    fetchCompanies(page, 50, debouncedSearchTerm || undefined);
  }, [page, debouncedSearchTerm, fetchCompanies]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this company?")) {
      return;
    }

    try {
      setDeletingCompanyId(id);
      await deleteCompany(id);
    } catch (err: any) {
      alert(err.message || "Failed to delete company");
    } finally {
      setDeletingCompanyId(null);
    }
  };

  if (loading && companies.length === 0) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full flex-col bg-default-background">
      <div className="flex w-full flex-col items-start gap-6 p-12">
        <div className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Companies
            </span>
            <span className="text-body font-body text-subtext-color">
              Manage all tenant companies
            </span>
          </div>
          <Button
            icon={<FeatherPlus />}
            onClick={() => navigate("/admin/companies/create")}
          >
            Create Company
          </Button>
        </div>

        {error && (
          <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-sm font-medium text-red-800">
              {error.message}
            </span>
          </div>
        )}

        <div className="flex w-full flex-col items-start gap-4">
          <TextField
            className="w-full max-w-md"
            variant="filled"
            label=""
            helpText=""
            icon={<FeatherSearch />}
          >
            <TextField.Input
              placeholder="Search by company name, subdomain, or Zoho Org ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </TextField>
        </div>

        <div className="flex w-full flex-col items-start gap-4">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>Company Name</Table.HeaderCell>
                <Table.HeaderCell>Subdomain</Table.HeaderCell>
                <Table.HeaderCell>Zoho Org ID</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {companies.map((company) => (
              <Table.Row key={company.id}>
                <Table.Cell>
                  <button
                    onClick={() => navigate(`/admin/companies/${company.id}`)}
                    className="text-body font-body text-default-font hover:text-brand-600 hover:underline cursor-pointer"
                  >
                    {company.company_name}
                  </button>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-subtext-color">
                    {company.app_url}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-body font-body text-subtext-color">
                    {company.zoho_org_id || "-"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant={company.is_active ? "success" : "error"}>
                    {company.is_active ? "Active" : "Inactive"}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="neutral-tertiary"
                      size="small"
                      icon={<FeatherEye />}
                      onClick={() => navigate(`/admin/companies/${company.id}`)}
                    >
                      View
                    </Button>
                    {company.is_active && (
                      <Button
                        variant="destructive-tertiary"
                        size="small"
                        icon={<FeatherTrash2 />}
                        onClick={() => handleDelete(company.id)}
                        loading={deletingCompanyId === company.id}
                        disabled={deletingCompanyId === company.id || loading}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>

          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="neutral-secondary"
                size="small"
                icon={<FeatherChevronLeft />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-body font-body text-default-font px-4">
                Page {page} of {pagination.total_pages}
              </span>
              <Button
                variant="neutral-secondary"
                size="small"
                iconRight={<FeatherChevronRight />}
                onClick={() =>
                  setPage((p) => Math.min(pagination.total_pages, p + 1))
                }
                disabled={page === pagination.total_pages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
